import * as Sentry from "@sentry/react";
import { ApiError } from "./auth-api";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

/**
 * Wzorce bledow ktorych NIE chcemy widziec w Sentry — to nie sa bugi w kodzie,
 * tylko transient stany sieci/sesji generujace szum (i zuzywajace replay quota).
 *
 * Eksportowane do unit testow zeby zagwarantowac ze rzeczywiste komunikaty z
 * produkcji sa lapane. Dodawaj tu wzorce gdy widzisz powtarzajacy sie issue
 * ktory NIE jest bugiem (np. user offline, JWT wygasl, refresh race).
 */
export const IGNORED_ERROR_PATTERNS: RegExp[] = [
	// Network errors — user offline lub server timeout
	/^Load failed/, // Safari
	/^Failed to fetch/, // Chrome
	/^NetworkError when attempting to fetch resource/, // Firefox
	// To samo z okna „Zgłoś problem” (station-report-api.ts opakowuje błąd fetch).
	// Okno ponawia kontekst co 30 s, więc bez sieci każda próba byłaby zdarzeniem.
	// FARMASTER-FRONTEND-47.
	/^Brak połączenia z Farmasterem$/,
	// Auth state — sesja wygasla, refresh cookie zuzyty, race miedzy zakladkami
	// (auth-api.ts:71-75 opisuje scenariusz). Frontend i tak emituje
	// auth:session-expired event ktory powinien zalogowac usera ponownie.
	/^Sesja wygasla/,
	/^Tenant not identified/,
	// WebView odświeża panel po odzyskaniu sesji; wyjątek tylko przerywa stare
	// zapytanie po wysłaniu `panel:auth-expired`. FARMASTER-FRONTEND-3V.
	/^Odnawiam sesję panelu$/,
	// Wygasla lub odwolana sesja publicznego KSeF (/publiczne/ksef, TTL 30 dni,
	// podglad admina 1 h) przy otwartej karcie. Strona czysci sesje i wraca do
	// formularza kodu. Wzorzec celowo waski: "Brak autoryzacji [401 ...]" to brak
	// naglowka sesji, czyli regresja klienta, i ma dalej trafiac do Sentry.
	// FARMASTER-FRONTEND-43.
	/Autoryzacja wygasła \[401 \/ksef\/public\//,
	// Backend validation errors prezentowane w UI — to nie sa bugi, tylko
	// expected user-fault rzucone jako AppError::BadRequest. UI je pokazuje
	// (toast / dialog), ale fingerprint Sentry zlepial je z innymi ApiError
	// pod jednym issue (FE-1H/FE-1J 115094621/115599436).
	/Maksymalny zakres raportu/,
	/Brak konfiguracji przedzialow zalegania/,
	/Parametr idfirm jest wymagany/,
	// Guard wielkosci raportu (400) — user ma zawezic filtry, UI pokazuje
	// komunikat. FARMASTER-FRONTEND-2R.
	/Raport jest zbyt duzy do pokazania w przegladarce/,
	// Deep-link / recznie wpisany zakres poza oknem MV dashboardu (422) —
	// strona pokazuje stan "poza oknem". FARMASTER-FRONTEND-3Q.
	/Zakres dat poza obs/,
	// Filtr cennika wlasnego przy scope > 1 apteki (400) — walidacja backendu,
	// UI prowadzi usera do wyboru apteki. FARMASTER-FRONTEND-3P.
	/Filtr cennika wlasnego wymaga wybrania pojedynczej apteki/,
	// User odwrocil zakres dat w kalkulatorze marzy — backend odrzuca (BadRequest),
	// UI pokazuje toast. To user-fault walidacji, nie bug. FARMASTER-FRONTEND-35.
	/dateFrom późniejszy niż dateTo/,
	// To samo w raporcie zmian BLOZ, innym komunikatem z backendu. Strona sprawdza
	// zakres przed wyslaniem (`zakresDatOdwrocony`), wiec to siatka bezpieczenstwa
	// dla deep-linka albo innego wejscia. FARMASTER-FRONTEND-3K.
	/Data poczatkowa jest pozniejsza niz koncowa/,
	// Expected 403 when scope=wlasne but account has no own pharmacies (strateg-cenowy
	// layout sanitizes scope; rare race / stale localStorage still hits API). FARMASTER-FRONTEND-24.
	/Brak widocznosci na ten scope/,
	// Twarde bariery rolowe na backendzie ("Dostep do ... wymaga roli analyst,
	// super_admin lub purchaser") — widocznosc strony jest osobna, miekka warstwa,
	// wiec omylkowy grant pokazuje pusta strone i to zaprojektowane 403, nie bug.
	// FARMASTER-FRONTEND-3G (/kontrakty/rozliczenia/{id}).
	/wymaga roli/,
	// Ten sam mechanizm od strony endpointow super_admin-only (outbound / SFY).
	// Praktyczny wyzwalacz: podglad uzytkownika (impersonacja) uruchomiony gdy
	// super_admin stoi na /konfiguracja/sfy — token zmienia role, komponent
	// jeszcze wisi na ekranie, wiec kolejny refetch dostaje 403 zanim guard
	// zdazy przekierowac (auth-provider.tsx opisuje ten sam wyscig dla
	// /admin/*). FARMASTER-FRONTEND-3M / 3N.
	/Wymagana rola super_admin/,
	// Super_admin po przelaczeniu organizacji (X-Tenant-Override) otwiera zakladke
	// Helpdesk → Konfiguracja → Raport dzienny: wypisanie dotyczy tylko wlasnej
	// organizacji, backend odmawia (403), a zakladka pokazuje powod.
	/^Raport dzienny: wypisać się można tylko we własnej organizacji$/,
];

/** Expected authz/validation errors — do not burn Sentry quota via captureException. */
export function shouldCaptureQueryError(error: unknown): boolean {
	if (error instanceof ApiError && error.status === 503 && error.code === "DATA_PREPARING") {
		return false;
	}
	if (error instanceof Error) {
		if (IGNORED_ERROR_PATTERNS.some((re) => re.test(error.message))) {
			return false;
		}
	}
	return true;
}

export function initSentry() {
	// Three independent guards — any one blocks Sentry in dev:
	// 1. Vite dev server sets DEV=true
	// 2. DSN empty/missing (dev.sh sets VITE_SENTRY_DSN="")
	// 3. beforeSend rejects non-farmaster.pl URLs
	if (import.meta.env.DEV || !SENTRY_DSN) {
		return;
	}

	Sentry.init({
		dsn: SENTRY_DSN,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "production",
		release: import.meta.env.VITE_SENTRY_RELEASE,
		ignoreErrors: IGNORED_ERROR_PATTERNS,
		// SDK 11 bez tej opcji zbiera wszystko: IP, cookies, naglowki oraz body
		// zapytan i odpowiedzi fetch — a odpowiedzi API niosa ceny zakupu i dane
		// aptek. Zakres SDK 10 (sendDefaultPii wylaczone), przepisany z przewodnika
		// migracji 10 -> 11. Uzytkownik z syncSentryUser() idzie dalej (setUser
		// jest zawsze wysylany), `userInfo` steruje tylko danymi zgadywanymi przez SDK.
		dataCollection: {
			userInfo: false,
			cookies: false,
			httpHeaders: {
				request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
				response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
			},
			httpBodies: [],
			urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
			genAI: { inputs: false, outputs: false },
			databaseQueryData: false,
			graphQL: { document: false, variables: false },
		},
		// Transakcje jak w SDK 10. W domyslnym strumieniowaniu spanow SDK 11 nie
		// wywoluje beforeSendTransaction, wiec filtr farmaster.pl nizej przestalby
		// dzialac, a nazwy i operacje spanow w Sentry zmienilyby sie pod alertami.
		traceLifecycle: "static",
		// SDK 11 dokleja sztuczny stack do captureMessage i nie-Error w
		// captureException, co tworzy nowe grupy issue. Zostajemy przy SDK 10.
		attachStacktrace: false,
		integrations: [
			Sentry.browserTracingIntegration(),
			// SDK 11 tworzy sesje tylko przy zaladowaniu strony; release health
			// liczymy jak dotad, z nowa sesja przy nawigacji SPA.
			Sentry.browserSessionIntegration({ lifecycle: "route" }),
			// Replay nagrywal CALY renderowany tekst i media — a na ekranach
			// konfiguracji widac odslonione poswiadczenia urzadzen, podglad kluczy
			// API i ceny zakupu aptek (F25 skanu bezpieczenstwa 2026-08-08).
			// Domyslne maskowanie wlaczone; czytelnosc nagrania odzyskuje sie
			// punktowo przez `unmask` na konkretnym, niewrazliwym kontenerze.
			Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
		],
		tracesSampleRate: 0.2,
		replaysSessionSampleRate: 0,
		// 0.3 = ~15 replayow/mc przy obecnym ruchu bledow. Plan ma cap 50/mc,
		// po przekroczeniu Sentry dropuje nowe nagrania (pay-as-you-go disabled).
		// Stack trace + breadcrumby dalej zbierane na 100% — replay to nice-to-have.
		replaysOnErrorSampleRate: 0.3,
		allowUrls: [/https?:\/\/(app|api)\.farmaster\.pl/],
		beforeSend(event) {
			const url = event.request?.url || window.location.href;
			if (!/farmaster\.pl/.test(url)) {
				return null;
			}
			const excMsg = event.exception?.values?.[0]?.value ?? "";
			if (IGNORED_ERROR_PATTERNS.some((re) => re.test(excMsg))) {
				return null;
			}
			// Fingerprint per (exception type + first ~80 chars of message).
			// Bez tego wszystkie ApiError z `auth-api.ts:224 apiFetchWithBase`
			// laduja w jednym issue (FE-1H 115094621 mial 80 events skladajacych
			// sie z 5+ roznych root cause: max 92 dni, brak konfiguracji,
			// brak idfirm, internal 500-tki itp.). Po split jeden message =
			// jeden issue, tagi i breadcrumbs robia roboty.
			const exc = event.exception?.values?.[0];
			if (exc) {
				const type = exc.type ?? "Error";
				const msgKey = (exc.value ?? "").slice(0, 80).replace(/\d+/g, "N");
				event.fingerprint = ["{{ default }}", type, msgKey];
			}
			return event;
		},
		beforeSendTransaction(event) {
			const url = event.request?.url || window.location.href;
			if (!/farmaster\.pl/.test(url)) {
				return null;
			}
			return event;
		},
	});
}

/**
 * Powiazanie eventow Sentry z zalogowanym kontem — bez tego mail z webhooka
 * (sekcja "Uzytkownik" w sentry_webhook.rs) nigdy sie nie renderuje, bo eventy
 * frontendu ida anonimowo. Wolane z AuthProvider przy kazdej zmianie usera.
 *
 * Podczas podgladu (impersonacji) eventowi przypisujemy PODGLADANE konto —
 * to w jego kontekscie uprawnien/scope blad powstal — a operatora niesie tag
 * `impersonated_by`, zeby mail nie obciazyl niewinnego konta apteki.
 * Safe to call before initSentry — @sentry/react no-ops when not initialized.
 */
export function syncSentryUser(
	user: { id: number; email: string; name?: string } | null,
	impersonatedBy: number | null,
) {
	if (!user) {
		Sentry.setUser(null);
		Sentry.setTag("impersonated_by", undefined);
		return;
	}
	Sentry.setUser({
		id: String(user.id),
		email: user.email,
		username: user.name || undefined,
	});
	Sentry.setTag("impersonated_by", impersonatedBy != null ? String(impersonatedBy) : undefined);
}

/**
 * Semantic breadcrumb for DataTable toolbar interactions.
 * Safe to call before initSentry — @sentry/react no-ops when not initialized.
 */
export function tableBreadcrumb(action: string, data?: Record<string, unknown>) {
	Sentry.addBreadcrumb({
		category: "table",
		message: action,
		level: "info",
		data,
	});
}

export { Sentry };
